export enum SideEnum {
  PRO = 'pro',
  CON = 'con',
}

export enum WeightEnum {
  LOW = 0,
  HIGH = 1,
}

export interface ReasonType {
  optionId: string
  id: string
  side: SideEnum
  label: string
  weight: WeightEnum
  aiGenerated?: boolean
}

export interface OptionType {
  id: string
  label: string
}

export interface ListType {
  reasons: ReasonType[]
  options: OptionType[]
}

export interface SideWeights {
  pro: number
  con: number
  total: number
}

export interface OptionScore {
  optionId: string
  pro: number
  con: number
  total: number
  proRatio: number
  proPercentage: string
}

export type ListAction =
  | { type: 'option/add'; option: OptionType }
  | { type: 'option/edit'; id: string; label: string }
  | { type: 'option/delete'; id: string }
  | { type: 'reason/add'; reason: ReasonType }
  | { type: 'reason/edit'; id: string; label: string }
  | { type: 'reason/delete'; id: string }
  | { type: 'reason/setWeight'; id: string; weight: WeightEnum }
  | { type: 'list/replace'; list: ListType }
  | { type: 'list/clear' }

export const emptyList: ListType = {
  reasons: [],
  options: [],
}

export function createOption(label: string, id: string): OptionType {
  return {
    id,
    label: label.trim(),
  }
}

export function createReason(input: {
  optionId: string
  id: string
  side: SideEnum
  label: string
  weight?: WeightEnum
  aiGenerated?: boolean
}): ReasonType {
  return {
    optionId: input.optionId,
    id: input.id,
    side: input.side,
    label: input.label.trim(),
    weight: input.weight ?? WeightEnum.LOW,
    aiGenerated: input.aiGenerated,
  }
}

export function listReducer(state: ListType, action: ListAction): ListType {
  switch (action.type) {
    case 'option/add':
      return {
        ...state,
        options: [...state.options, action.option],
      }
    case 'option/edit':
      return {
        ...state,
        options: state.options.map((option) =>
          option.id === action.id ? { ...option, label: action.label } : option,
        ),
      }
    case 'option/delete':
      return {
        ...state,
        options: state.options.filter((option) => option.id !== action.id),
        reasons: state.reasons.filter(
          (reason) => reason.optionId !== action.id,
        ),
      }
    case 'reason/add':
      return {
        ...state,
        reasons: [...state.reasons, action.reason],
      }
    case 'reason/edit':
      return {
        ...state,
        reasons: state.reasons.map((reason) =>
          reason.id === action.id ? { ...reason, label: action.label } : reason,
        ),
      }
    case 'reason/delete':
      return {
        ...state,
        reasons: state.reasons.filter((reason) => reason.id !== action.id),
      }
    case 'reason/setWeight':
      return {
        ...state,
        reasons: state.reasons.map((reason) =>
          reason.id === action.id
            ? { ...reason, weight: action.weight }
            : reason,
        ),
      }
    case 'list/replace':
      return action.list
    case 'list/clear':
      return emptyList
  }
}

export function getReasonsByOptionId(
  list: ListType,
  optionId: string,
): ReasonType[] {
  return list.reasons.filter((reason) => reason.optionId === optionId)
}

export function calculateSideWeights(reasons: ReasonType[]): SideWeights {
  return reasons.reduce<SideWeights>(
    (weights, reason) => {
      const value = reason.weight + 1

      if (reason.side === SideEnum.PRO) {
        return {
          ...weights,
          pro: weights.pro + value,
          total: weights.total + value,
        }
      }

      return {
        ...weights,
        con: weights.con + value,
        total: weights.total + value,
      }
    },
    { pro: 0, con: 0, total: 0 },
  )
}

export function calculateProWeightRatio(reasons: ReasonType[]): number {
  const weights = calculateSideWeights(reasons)

  if (weights.total === 0) {
    return 0.5
  }

  return weights.pro / weights.total
}

export function calculateProWeightPercentage(reasons: ReasonType[]): string {
  return `${calculateProWeightRatio(reasons) * 100}%`
}

export function calculateOptionScore(
  list: ListType,
  optionId: string,
): OptionScore {
  const reasons = getReasonsByOptionId(list, optionId)
  const weights = calculateSideWeights(reasons)
  const proRatio = calculateProWeightRatio(reasons)

  return {
    optionId,
    ...weights,
    proRatio,
    proPercentage: `${Math.round(proRatio * 100)}%`,
  }
}

export function getLeadingOption(list: ListType): OptionScore | undefined {
  return list.options
    .map((option) => calculateOptionScore(list, option.id))
    .sort((left, right) => {
      if (right.proRatio !== left.proRatio) {
        return right.proRatio - left.proRatio
      }

      return right.total - left.total
    })[0]
}

export const starterList: ListType = {
  options: [
    createOption('Take the new job', 'job'),
    createOption('Stay where I am', 'stay'),
  ],
  reasons: [
    createReason({
      optionId: 'job',
      id: 'growth',
      side: SideEnum.PRO,
      label: 'More room to grow',
      weight: WeightEnum.HIGH,
    }),
    createReason({
      optionId: 'job',
      id: 'unknowns',
      side: SideEnum.CON,
      label: 'A lot of unknowns',
    }),
    createReason({
      optionId: 'stay',
      id: 'stability',
      side: SideEnum.PRO,
      label: 'Stable team and routine',
    }),
    createReason({
      optionId: 'stay',
      id: 'stale',
      side: SideEnum.CON,
      label: 'Feels a bit stale',
      weight: WeightEnum.HIGH,
    }),
  ],
}
